import {
  ActionIcon,
  AppShell,
  Burger,
  Button,
  Group,
  Loader,
  Menu,
  NavLink,
  Stack,
  Text,
  Title,
  VisuallyHidden,
  useMantineColorScheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import type React from 'react';
import { Suspense, lazy } from 'react';
import { Link, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { ApiError, api, clearAllAuth, getStoredToken, getStoredUser } from './api.js';
// Hot pages stay eagerly imported (loaded on first paint).
// AddinPage is heavy (~1700 lines + all sub-panels) and only loads when
// ?ctx=addin is set in the URL — the COM addin's WebView2. Dashboard
// users on /home or /admin never reach it, so it's worth lazy-loading.
const AddinPage = lazy(() => import('./pages/AddinPage.js').then((m) => ({ default: m.AddinPage })));
import { GetStartedPage } from './pages/GetStartedPage.js';
import { HomePage } from './pages/HomePage.js';
import { LoginPage } from './pages/LoginPage.js';
import { RegisterPage } from './pages/RegisterPage.js';
import { SetupPage } from './pages/SetupPage.js';
// Round 24 R24-5: lazy-load secondary pages so the initial bundle ships
// only the home + auth routes. Each page becomes its own async chunk
// fetched on navigation.
const AccountDetailPage = lazy(() => import('./pages/AccountDetailPage.js').then((m) => ({ default: m.AccountDetailPage })));
const AccountsPage = lazy(() => import('./pages/AccountsPage.js').then((m) => ({ default: m.AccountsPage })));
const AuditPage = lazy(() => import('./pages/AuditPage.js').then((m) => ({ default: m.AuditPage })));
const CachePage = lazy(() => import('./pages/CachePage.js').then((m) => ({ default: m.CachePage })));
const CalibrationPage = lazy(() => import('./pages/CalibrationPage.js').then((m) => ({ default: m.CalibrationPage })));
const CronPage = lazy(() => import('./pages/CronPage.js').then((m) => ({ default: m.CronPage })));
const GoldenTestPage = lazy(() => import('./pages/GoldenTestPage.js').then((m) => ({ default: m.GoldenTestPage })));
const HealthPage = lazy(() => import('./pages/HealthPage.js').then((m) => ({ default: m.HealthPage })));
const KillswitchPage = lazy(() => import('./pages/KillswitchPage.js').then((m) => ({ default: m.KillswitchPage })));
const MessagesPage = lazy(() => import('./pages/MessagesPage.js').then((m) => ({ default: m.MessagesPage })));
const MinedRulesPage = lazy(() => import('./pages/MinedRulesPage.js').then((m) => ({ default: m.MinedRulesPage })));
const NewAccountWizard = lazy(() => import('./pages/NewAccountWizard.js').then((m) => ({ default: m.NewAccountWizard })));
const ProposedRulesPage = lazy(() => import('./pages/ProposedRulesPage.js').then((m) => ({ default: m.ProposedRulesPage })));
const SecurityPage = lazy(() => import('./pages/SecurityPage.js').then((m) => ({ default: m.SecurityPage })));
const SendersPage = lazy(() => import('./pages/SendersPage.js').then((m) => ({ default: m.SendersPage })));
const SenderDetailPage = lazy(() => import('./pages/SenderDetailPage.js').then((m) => ({ default: m.SenderDetailPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage.js').then((m) => ({ default: m.SettingsPage })));
const RoutingStudioPage = lazy(() => import('./pages/RoutingStudioPage.js').then((m) => ({ default: m.RoutingStudioPage })));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage.js').then((m) => ({ default: m.AnalyticsPage })));
const UsersPage = lazy(() => import('./pages/UsersPage.js').then((m) => ({ default: m.UsersPage })));
const AddinReleasePage = lazy(() => import('./pages/AddinReleasePage.js').then((m) => ({ default: m.AddinReleasePage })));
const BroadcastsPage = lazy(() => import('./pages/BroadcastsPage.js').then((m) => ({ default: m.BroadcastsPage })));
const SpendPage = lazy(() => import('./pages/SpendPage.js').then((m) => ({ default: m.SpendPage })));
const CommandCenterPage = lazy(() => import('./pages/CommandCenterPage.js').then((m) => ({ default: m.CommandCenterPage })));

interface NavGroup {
  label: string;
  items: Array<{ to: string; label: string }>;
}
const NAV: NavGroup[] = [
  {
    label: 'Today',
    items: [
      { to: '/', label: 'Overview' },
      { to: '/command-center', label: 'Command Center' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/accounts', label: 'Accounts' },
      { to: '/messages', label: 'Messages' },
      { to: '/senders', label: 'Senders' },
      { to: '/admin/proposed-rules', label: 'Proposed rules' },
      { to: '/observability/cron', label: 'Cron state' },
    ],
  },
  {
    label: 'Observability',
    items: [
      { to: '/analytics', label: 'Communication analytics' },
      { to: '/observability/spend', label: 'LLM spend' },
      { to: '/observability/cache', label: 'Cache hit rate' },
      { to: '/observability/calibration', label: 'Calibration' },
      { to: '/observability/golden-test', label: 'Golden test' },
      { to: '/observability/mined-rules', label: 'Mined rules' },
      { to: '/admin/audit', label: 'Audit log' },
      { to: '/observability/health', label: 'Health' },
    ],
  },
  {
    label: 'Admin',
    items: [
      { to: '/users', label: 'Users' },
      { to: '/admin/release', label: 'Addin release' },
      { to: '/observability/broadcasts', label: 'Broadcasts' },
      { to: '/settings', label: 'Settings' },
      { to: '/routing', label: 'Routing studio' },
      { to: '/security', label: 'Security' },
      { to: '/killswitch', label: 'Kill switch' },
    ],
  },
];

export function App(): React.JSX.Element {
  const [opened, { toggle }] = useDisclosure();
  const token = getStoredToken();
  const user = getStoredUser();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const nav = useNavigate();
  const loc = useLocation();

  // The addin path /addin/* is reserved for the brain's REST API (Caddy
  // routes that prefix to the brain). The dashboard advertises the addin UI
  // via the `?ctx=addin` query param on the root path. Detect that and
  // render AddinPage without the dashboard AppShell — that's the modern
  // Mantine UI hosted inside the COM addin's WebView2 control.
  const isAddinContext = new URLSearchParams(loc.search).get('ctx') === 'addin';
  if (isAddinContext) {
    return (
      <Suspense fallback={<Loader />}>
        <AddinPage />
      </Suspense>
    );
  }

  if (!token || !user) {
    return (
      <Routes>
        <Route path="/setup" element={<SetupPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/get-started" element={<GetStartedPage />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }
  // Round 28-AC: end-users with global_role='none' (self-registered via
  // /register) get the addin-only landing page. They CANNOT see the
  // admin portal — every dashboard endpoint is gated server-side by
  // requireRole, so even if they typed a URL the server would 403.
  // The route guard here is purely UX so they don't see "forbidden"
  // shouting at them on every page.
  if (user.global_role === 'none') {
    return (
      <Routes>
        <Route path="/get-started" element={<GetStartedPage />} />
        <Route path="*" element={<GetStartedPage />} />
      </Routes>
    );
  }

  const onLogout = async () => {
    try {
      await api.authLogout();
    } catch (e) {
      // Logout is best-effort — even if the brain is unreachable we wipe local state.
      if (!(e instanceof ApiError)) {
        notifications.show({ color: 'yellow', message: 'Logout reached, server unresponsive.' });
      }
    } finally {
      clearAllAuth();
      nav('/');
    }
  };

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 240, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="sm"
              size="sm"
              aria-label="toggle navigation"
            />
            <Title order={4}>ai-outlook · admin</Title>
          </Group>
          <Group>
            <Menu position="bottom-end" withArrow>
              <Menu.Target>
                <Button variant="subtle" size="sm">
                  {user.email} · {user.global_role}
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>{user.display_name}</Menu.Label>
                <Menu.Item component={Link} to="/security">
                  Security
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item color="red" onClick={onLogout}>
                  Sign out
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
            <ActionIcon
              variant="default"
              onClick={() => toggleColorScheme()}
              aria-label={`switch to ${colorScheme === 'dark' ? 'light' : 'dark'} mode`}
              title={`switch to ${colorScheme === 'dark' ? 'light' : 'dark'} mode`}
            >
              <span aria-hidden="true">{colorScheme === 'dark' ? '☀' : '🌙'}</span>
              <VisuallyHidden>
                switch to {colorScheme === 'dark' ? 'light' : 'dark'} mode
              </VisuallyHidden>
            </ActionIcon>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="sm">
        <NavLinks />
      </AppShell.Navbar>

      <AppShell.Main>
        <Suspense fallback={<Loader />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/accounts" element={<AccountsPage />} />
          <Route path="/accounts/new" element={<NewAccountWizard />} />
          <Route path="/accounts/:id" element={<AccountDetailPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/routing" element={<RoutingStudioPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/devices" element={<SecurityPage />} />
          <Route path="/observability/spend" element={<SpendPage />} />
          <Route path="/observability/cache" element={<CachePage />} />
          <Route path="/observability/calibration" element={<CalibrationPage />} />
          <Route path="/observability/golden-test" element={<GoldenTestPage />} />
          <Route path="/observability/mined-rules" element={<MinedRulesPage />} />
          <Route path="/observability/cron" element={<CronPage />} />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/senders" element={<SendersPage />} />
          <Route path="/admin/senders" element={<SendersPage />} />
          <Route path="/admin/senders/:email" element={<SenderDetailPage />} />
          <Route path="/admin/audit" element={<AuditPage />} />
          <Route path="/admin/release" element={<AddinReleasePage />} />
          <Route path="/observability/broadcasts" element={<BroadcastsPage />} />
          <Route path="/admin/proposed-rules" element={<ProposedRulesPage />} />
          {/* /observability/health is the canonical URL (Caddy routes
              /admin/* to the brain, so /admin/health 404s on direct visit).
              /admin/health kept for backwards-compat in case any link still
              points there from inside the SPA. */}
          <Route path="/observability/health" element={<HealthPage />} />
          <Route path="/admin/health" element={<HealthPage />} />
          <Route path="/killswitch" element={<KillswitchPage />} />
          <Route path="/command-center" element={<CommandCenterPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </AppShell.Main>
    </AppShell>
  );
}

function NavLinks(): React.JSX.Element {
  const loc = useLocation();
  return (
    <Stack gap="md">
      {NAV.map((g) => (
        <Stack key={g.label} gap={0}>
          <Text size="xs" c="dimmed" tt="uppercase" px="sm" pb={4}>
            {g.label}
          </Text>
          {g.items.map((n) => {
            const active =
              n.to === '/'
                ? loc.pathname === '/'
                : loc.pathname === n.to || loc.pathname.startsWith(`${n.to}/`);
            return (
              <NavLink
                key={n.to}
                component={Link}
                to={n.to}
                label={n.label}
                active={active}
              />
            );
          })}
        </Stack>
      ))}
    </Stack>
  );
}

function NotFound(): React.JSX.Element {
  return (
    <Stack>
      <Title order={3}>Not found</Title>
      <Text>The page you requested does not exist.</Text>
    </Stack>
  );
}
